import { useProducts } from "@/hooks/useProducts";
import { ProductCard } from "./ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";

const ProductGrid = ({ limit, asPageTitle }: { limit?: number; asPageTitle?: boolean }) => {
  const Eyebrow = asPageTitle ? "h1" : "p";
  const { data: allProducts, isLoading, error } = useProducts(20);
  const singles = allProducts?.filter((p) => !/gift set/i.test(p.node.title));
  const products = limit ? singles?.slice(0, limit) : singles;
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollReveal();

  return (
    <section id="collection" className="py-24 lg:py-32 bg-secondary/30">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 lg:mb-20 transition-all duration-700 ease-out ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <Eyebrow className="text-luxury text-muted-foreground mb-4">{asPageTitle ? "Refillable Perfume Atomiser Collection" : "The Collection"}</Eyebrow>
          <h2 className="heading-section text-foreground mb-6">
            Eight Colours,
            <br />
            <span className="text-gold italic">Infinite Style</span>
          </h2>
          <p className="text-body text-muted-foreground max-w-2xl mx-auto">
            Each atomizer is wrapped in Premium pebbled vegan leather, available in a
            curated palette designed to complement your personal aesthetic.
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
            {Array.from({ length: limit ?? 8 }).map((_, i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="aspect-[3/4] w-full rounded-sm" />
                <Skeleton className="h-6 w-3/4 mx-auto" />
                <Skeleton className="h-4 w-1/2 mx-auto" />
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Failed to load products. Please try again later.</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && products?.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">No products found</p>
            <p className="text-sm text-muted-foreground mt-2">Products will appear here once added to your store.</p>
          </div>
        )}

        {/* Product Grid */}
        {!isLoading && !error && products && products.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
            {products.map((product, index) => (
              <div
                key={product.node.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 80}ms`, animationFillMode: "both" }}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        {limit && products && products.length > 0 && (
          <div
            ref={ctaRef}
            className={`text-center mt-16 transition-all duration-700 ease-out ${
              ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <Link to="/shop" className="btn-luxury">
              View All Colours
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductGrid;
